import React, { useCallback, useEffect, useState } from 'react';
import { Icon } from './inboxIcons';

// Support → Customer photos: outfits customers shared ("Show us your outfit"), with what they agreed to.
// Only photos with public consent can be approved or marked used; REMOVE from the customer marks them removed.
const CONSENT = {
  name: ['Public, with name', 'cx-c-resolved'],
  anon: ['Public, no name', 'cx-c-resolved'],
  team: ['Team only', 'cx-c-waiting'],
  revoked: ['Removed by customer', 'cx-c-new'],
};
const STATUS = { new: 'New', approved: 'Approved', used: 'Used', removed: 'Removed' };
const USED = [['instagram', 'Instagram'], ['website', 'Website'], ['ads', 'Ads'], ['other', 'Other']];
const FILTERS = [['all', 'All'], ['usable', 'Can be used'], ['new', 'New'], ['approved', 'Approved'], ['used', 'Used'], ['removed', 'Removed']];
const when = (ms) => (ms ? new Date(ms).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '');

function Photo({ p, apiBase, getToken, api, canManage, onChanged }) {
  const [src, setSrc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let url = null;
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${apiBase}/api/v1/support/photos/${p.id}/image`, { headers: { Authorization: `Bearer ${await getToken()}` } });
        if (!res.ok) throw new Error('Image not available');
        url = URL.createObjectURL(await res.blob());
        if (alive) setSrc(url);
      } catch (e) { if (alive) setSrc(false); }
    })();
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [p.id, apiBase, getToken]);
  const save = async (body) => {
    setBusy(true); setErr(null);
    try { await api(`/api/v1/support/photos/${p.id}`, 'POST', body); onChanged(); } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const c = CONSENT[p.consent?.scope] || ['Waiting for consent', 'cx-c-plain'];
  const publicOk = ['name', 'anon'].includes(p.consent?.scope) && p.status !== 'removed';
  const video = String(p.mimeType || '').startsWith('video');
  return (
    <div className={`cx-ph${p.status === 'removed' ? ' removed' : ''}`}>
      <div className="cx-ph-img">
        {src === null ? <span className="cx-skel" style={{ width: '100%', height: '100%', margin: 0 }} /> : src === false ? <span className="cx-hint">Not available</span>
          : video ? <video src={src} controls preload="metadata" /> : <img src={src} alt={`Outfit from ${p.customerName || 'a customer'}`} />}
      </div>
      <div className="cx-ph-b">
        <div className="cx-ph-h"><b>{p.customerName || 'Customer'}</b><span className="cx-hint">{when(p.receivedAtMs)}</span></div>
        <div className="cx-hint">{p.fabric ? `${p.fabric} fabric` : 'Fabric not known'}</div>
        <div className="cx-ph-chips">
          <span className={`cx-chip ${c[1]}`}>{c[0]}{p.consent?.atMs ? ` · ${when(p.consent.atMs)}` : ''}</span>
          <span className="cx-chip cx-c-plain">{STATUS[p.status] || p.status}</span>
          {(p.usedOn || []).map((u) => <span key={u} className="cx-chip cx-c-plain">{USED.find(([k]) => k === u)?.[1] || u}</span>)}
        </div>
        {canManage && p.status !== 'removed' && (
          <div className="cx-ph-act">
            {p.status === 'new' && <button className="cx-btn cx-btn-sm" onClick={() => save({ status: 'approved' })} disabled={busy || !publicOk} title={publicOk ? '' : 'The customer did not agree to public use'}>Approve</button>}
            {USED.slice(0, 3).map(([k, l]) => (
              <button key={k} className={`cx-btn cx-btn-sm${(p.usedOn || []).includes(k) ? ' cx-btn-primary' : ''}`} disabled={busy || !publicOk}
                onClick={() => { const on = (p.usedOn || []).includes(k); const usedOn = on ? p.usedOn.filter((x) => x !== k) : [...(p.usedOn || []), k]; save({ usedOn, status: usedOn.length ? 'used' : 'approved' }); }}>{l}</button>
            ))}
            {src && <a className="cx-btn cx-btn-sm" href={src} download={`outfit-${p.id}${video ? '.mp4' : '.jpg'}`}><Icon name="down" size={14} />Download</a>}
          </div>
        )}
        {err && <div className="cx-err">{err}</div>}
      </div>
    </div>
  );
}

function CustomerPhotosPage({ api, apiBase, getToken }) {
  const [filter, setFilter] = useState('all');
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const load = useCallback(() => { api(`/api/v1/support/photos?status=${filter}`).then(setD).catch((e) => setErr(e.message)); }, [api, filter]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="cx-page">
      <div className="cx-page-in">
        <div className="cx-page-head">
          <div>
            <h1>Customer photos</h1>
            <p className="cx-lede">Outfits customers made with our fabric, shared on WhatsApp after "Show us your outfit". Only photos with public consent can be approved or used.</p>
          </div>
        </div>
        <div className="cx-seg" role="group" aria-label="Show" style={{ margin: '0 0 16px', maxWidth: 620 }}>
          {FILTERS.map(([k, l]) => <button key={k} aria-pressed={filter === k} onClick={() => setFilter(k)}>{l}</button>)}
        </div>
        {err ? <div className="cx-empty"><strong>Couldn't load photos</strong>{err}</div>
          : !d ? <div className="cx-skel" style={{ height: 240 }} />
            : !d.photos.length ? <div className="cx-empty"><strong>No photos yet</strong>They appear here when customers share their outfit after the "Show us your outfit" message (WhatsApp → Automations → Buy again).</div>
              : <div className="cx-ph-grid">{d.photos.map((p) => <Photo key={p.id} p={p} api={api} apiBase={apiBase} getToken={getToken} canManage={d.canManage} onChanged={load} />)}</div>}
      </div>
    </div>
  );
}

export default CustomerPhotosPage;
